import { Progress } from '@heroui/react'
import { useQuery } from '@tanstack/react-query'
import PulsingLight from 'assets/gifs/PulsingLight.gif'
import CloseIcon from 'assets/icons/close-icon-black.svg'
import ServerIcon from 'assets/icons/server-icon.svg'
import axios from 'axios'
import BillingAndPayments from 'components/AccountSupport/Billing/PaymentMethod'
import ActionBar from 'components/core/ActionBar'
import Button from 'components/core/Button'
import Checkbox from 'components/core/Checkbox'
import Input from 'components/core/Input'
import Modal from 'components/core/Modal'
import { PageTitle } from 'components/core/PageTitle'
import { PageWrapper } from 'components/core/PageWrapper'
import Tabs from 'components/core/Tabs'
import Checkout from 'components/StripeElements/CheckoutForm/Checkout'
import config from 'config'
import { formatDistanceToNow } from 'date-fns'
import { useAuth } from 'hooks/useAuth'
import { CircleSlash, DollarSign, Info } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { getAuthToken } from 'utils/funcs/browser/getAuthToken'
import { getOrgId } from 'utils/funcs/browser/getOrgId'
import {
  generateInvoicePDF,
  getCreditPurchases,
  purchaseCredits,
} from 'utils/funcs/purchase-credits/purchaseCredits'
import 'react-toastify/dist/ReactToastify.css'

// Plan tier constants
const RATE_LIMITS = [
  {
    value: 'start',
    label: 'Start',
    hourly: '100',
    hourly_val: 100,
    daily_val: 100,
    daily: '100',
    concurrent: '10',
    price: 'Default',
    rate_limit_template: 5,
    voice_limit: 1,
  },
  {
    value: 'build',
    label: 'Build',
    daily: '2,000',
    daily_val: 2000,
    hourly: '1,000',
    hourly_val: 1000,
    concurrent: '50',
    price: '$299',
    rate_limit_template: 6,
    voice_limit: 5,
  },
  {
    value: 'scale',
    label: 'Scale',
    daily: '5,000',
    daily_val: 5000,
    hourly_val: 1000,
    hourly: '1,000',
    concurrent: '100',
    price: '$499',
    rate_limit_template: 7,
    voice_limit: 15,
  },
  {
    value: 'enterprise',
    label: 'Enterprise',
    daily: 'Unlimited',
    daily_val: 100000,
    hourly_val: 10000,
    hourly: 'Unlimited',
    concurrent: 'Unlimited',
    price: 'Contact Us',
    rate_limit_template: [2, 3],
    voice_limit: 'Unlimited',
  },
]

export default function PurchaseCredits() {
  const { credits, permissions, refetchCredits, org } = useAuth()
  const orgId = getOrgId()

  // Credit Purchase States
  const [creditPurchaseLoading, setCreditPurchaseLoading] = useState(false)
  const [creditPurchases, setCreditPurchases] = useState([])
  const [loadingPurchases, setLoadingPurchases] = useState(false)
  const [showCheckout, setShowCheckout] = useState(false)
  const [clientSecret, setClientSecret] = useState(null)
  const [clickedCreditIndex, setClickedCreditIndex] = useState()
  const [stripeCustomer, setStripeCustomer] = useState(null)

  // Plan Upgrade States
  const [loadingPlanIndex, setLoadingPlanIndex] = useState(-1)
  const [prePurchaseModal, setPrePurchaseModal] = useState(false)
  const [cancelModal, setCancelModal] = useState(false)
  const [focusedPlan, setFocusedPlan] = useState({
    rateLimit: null,
    index: null,
  })
  const [userRateLimitTemplate, setUserRateLimitTemplate] = useState(null)

  const hasPermissions
      = useMemo(() => permissions?.includes('owner') || permissions?.includes('admin'), [permissions])

  const { data: rateLimits, isLoading: isLoadingRateLimits } = useQuery({
    queryKey: ['rateLimits'],
    queryFn: async () => {
      const { data } = await $fetch(`/user/rateLimits`)
      return data
    },
  })

  // Credit Purchase Form
  const creditMethods = useForm({
    defaultValues: {
      amount: 50,
      save_details: true,
    },
  })

  const currAmount = creditMethods.watch('amount')

  const creditOptions = [
    {
      amount: 10,
      description:
        '110+ minutes of call time. Priority queue. Reduced latency.',
    },
    {
      amount: 20,
      description:
        '220+ minutes of call time. Priority queue. Reduced latency.',
    },
    {
      amount: 50,
      description:
        'Almost 9 hours of call time. Priority queue. Reduced latency.',
    },
  ]

  // Fetch Stripe customer details
  const fetchStripeDetails = async () => {
    try {
      const res = await $fetch('/billing/customer')
      if (res?.data?.customer) {
        setStripeCustomer(res.data.customer)
      }
    }
    catch (error) {
      console.error('Failed to fetch stripe details:', error)
    }
  }

  // Credit Purchase Logic
  const handleGetCreditPurchases = async () => {
    try {
      setLoadingPurchases(true)
      const purchases = await getCreditPurchases()
      if (purchases && Array.isArray(purchases)) {
        purchases.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        )
        setCreditPurchases(purchases)
      }
    }
    catch (e) {
      console.error('Failed to fetch credit purchases:', e)
    }
    finally {
      setLoadingPurchases(false)
    }
  }

  const onCreditSubmit = async (formData) => {
    setCreditPurchaseLoading(true)

    if (permissions?.includes('owner') || permissions?.includes('admin')) {
      try {
        const res = await purchaseCredits(
          formData.amount,
          formData.save_details,
        )

        if (res.needsCard) {
          setClientSecret(res.clientSecret)
          setShowCheckout(true)
        }
        else {
          await handleGetCreditPurchases()
          await refetchCredits()
          toast.success('Credits purchased successfully')
        }
      }
      catch (error) {
        console.error('Error purchasing credits:', error)
        toast.error('Error purchasing credits')
      }
    }
    else {
      toast.error('You don\'t have permission to purchase credits.')
    }
    setCreditPurchaseLoading(false)
  }

  // Plan Upgrade Logic
  const refreshPlanData = async () => {
    setUserRateLimitTemplate(org?.org_rate_limit)
  }

  const handleCancelFromPlan = async () => {
    try {
      const res = await axios({
        url: `${config.API_ENDPOINT}/orgs/${orgId}/billing/session`,
        method: 'GET',
        headers: {
          'authorization': getAuthToken(),
          'content-type': 'application/json',
        },
      })

      window.open(res.data?.data?.redirect_url, '_blank')
      setLoadingPlanIndex(-1)
    }
    catch (error) {
      console.error('Failed to cancel plan:', error)
      setLoadingPlanIndex(-1)
      return toast.error('Unexpected Error.')
    }
  }
  const handleSubscribeToPlan = async (rateLimit, index) => {
    setLoadingPlanIndex(index)
    if (rateLimit.rate_limit_template === userRateLimitTemplate) {
      return await handleCancelFromPlan()
    }

    const type = rateLimit.rate_limit_template === 6
      ? 'subscribe_build'
      : rateLimit.rate_limit_template === 7
        ? 'subscribe_scale'
        : ''

    let url = ''
    if (type === 'subscribe_build') {
      url = `${config.BILLING_ENDPOINT}/subscribe_build`
    }
    else if (type === 'subscribe_scale') {
      url = `${config.BILLING_ENDPOINT}/subscribe_scale`
    }

    try {
      await $fetch(url, { method: 'POST' })
      await refreshPlanData()
      setLoadingPlanIndex(-1)
      toast.success('Successfully Scaled Up.')
      window.location.reload()
    }
    catch (error) {
      const errorMessage = error.data?.errors?.[0]?.message ?? 'An unexpected error occurred'
      toast.error(errorMessage)
    }
    finally {
      setLoadingPlanIndex(-1)
    }
  }

  const checkIfPlanDisabled = (rateLimit, index) => {
    let state = 'Contact Us'
    let isDisabled = false

    // If org is enterprise type
    if (org?.org_type === 'enterprise') {
      if (rateLimit.value === 'enterprise') {
        return { state: 'Current Plan', isDisabled: true }
      }
      // Disable all non-enterprise plans
      return { state: 'Lower Plan', isDisabled: true }
    }

    // For non-enterprise orgs, use existing rate limit template logic
    if (!userRateLimitTemplate) {
      return { state, isDisabled: true }
    }

    const user_act_rl_template = RATE_LIMITS.find((doc) => {
      if (userRateLimitTemplate === 2 || userRateLimitTemplate === 3) {
        return doc.value === 'enterprise'
      }
      return doc.rate_limit_template === userRateLimitTemplate
    })

    if (!user_act_rl_template) {
      return { state, isDisabled: true }
    }

    if (index === 3 && userRateLimitTemplate === 2) {
      return { state: 'On Enterprise Plan', isDisabled: true }
    }

    if (
      Array.isArray(user_act_rl_template.rate_limit_template)
      && index === 3
    ) {
      return { state: 'Contact Us', isDisabled: false }
    }

    if (
      rateLimit.rate_limit_template === user_act_rl_template.rate_limit_template
    ) {
      if (rateLimit.value === 'start') {
        state = 'Base Plan'
        isDisabled = true
      }
      else {
        state = 'Cancel Plan'
        isDisabled = false
      }
    }
    else {
      if (
        index !== 3
        && Array.isArray(user_act_rl_template.rate_limit_template)
      ) {
        return { state: 'Lower Scale', isDisabled: false }
      }
      if (
        user_act_rl_template.rate_limit_template > rateLimit.rate_limit_template
      ) {
        state = 'Lower Plan'
        isDisabled = true
      }
      else if (
        user_act_rl_template.rate_limit_template < rateLimit.rate_limit_template
      ) {
        state = 'Increase Scale'
        isDisabled = false
      }
    }

    return { state, isDisabled }
  }

  // Load data on component mount
  useEffect(() => {
    refreshPlanData()
    handleGetCreditPurchases()
    fetchStripeDetails()
  }, [])

  if (!hasPermissions) {
    return (
      <PageWrapper>
        <InsufficientPermissions />
      </PageWrapper>
    )
  }

  return (
    <PageWrapper style={{ height: '100%', backgroundColor: '#fbfbfa' }}>
      <ActionBar top>
        <PageTitle>Billing & Credits</PageTitle>
      </ActionBar>

      <div className="w-full gap-2 flex flex-col px-4">
        <div className="lg:flex-row lg:justify-evenly flex flex-col justify-start items-center gap-12 mb-3">
          <Progress
            label="Hourly limit"
            valueLabel={rateLimits && (<p className="text-xs ">{`${rateLimits?.hourlyCount} / ${rateLimits?.template?.hourly_limit}`}</p>)}
            maxValue={rateLimits?.template?.hourly_limit}
            showValueLabel
            isIndeterminate={isLoadingRateLimits}
            value={rateLimits?.hourlyCount}
            classNames={{
              label: 'text-xs ',
              indicator: '!duration-1000 bg-gradient-to-r from-green-400 to-red-700',
              track: 'h-2',
            }}
          />
          <Progress
            label="Daily limit"
            valueLabel={rateLimits && (<p className="text-xs">{`${rateLimits?.dailyCount} / ${rateLimits?.template?.daily_limit}`}</p>)}
            maxValue={rateLimits?.template?.daily_limit}
            showValueLabel
            isIndeterminate={isLoadingRateLimits}
            value={rateLimits?.dailyCount}
            classNames={{
              label: 'text-xs ',
              indicator: '!duration-1000 bg-gradient-to-r from-green-400 to-red-700',
              track: 'h-2',
            }}
          />
        </div>
      </div>

      <div className="p-4">
        <div className="mb-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <h2 className="text-sm font-semibold">Current Balance</h2>
          <div className="flex items-baseline">
            <span className="text-2xl font-bold">
              {Number.parseFloat(credits).toFixed(2)}
            </span>
            <span className="ml-1 text-gray-500">credits</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Bland bills at $0.09/minute, pro-rated to the exact second, and only for call time (dial time excluded).
          </p>
        </div>

        <Tabs
          relative
          tabs={[
            {
              item: 'Plan & Limits',
              panel: (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {RATE_LIMITS.map((rateLimit, index) => (
                    <div
                      key={index}
                      className={`bg-white p-4 rounded-lg border ${
                        userRateLimitTemplate === rateLimit.rate_limit_template
                        || (userRateLimitTemplate === 2 && rateLimit.value === 'enterprise')
                        || (userRateLimitTemplate === 3 && rateLimit.value === 'enterprise')
                          ? 'border-blue-500 shadow-md'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold">
                          {rateLimit.label}
                          {(rateLimit.rate_limit_template === userRateLimitTemplate
                            || (userRateLimitTemplate === 2 && rateLimit.value === 'enterprise')
                            || (userRateLimitTemplate === 3 && rateLimit.value === 'enterprise')) && (
                            <span className="ml-2 inline-block">
                              <img src={PulsingLight} height={14} width={14} alt="Active plan" />
                            </span>
                          )}
                        </h3>
                        <span className="font-bold text-gray-700">{rateLimit.price}</span>
                      </div>

                      <div className="mt-3 space-y-2 text-sm">
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-gray-600">Daily Cap:</span>
                          <span className="font-medium">
                            {rateLimit.daily}
                            {' '}
                            calls
                          </span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-gray-600">Hourly Cap:</span>
                          <span className="font-medium">
                            {rateLimit.hourly}
                            {' '}
                            calls
                          </span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-gray-600">Concurrency:</span>
                          <span className="font-medium">
                            {rateLimit.concurrent}
                            {' '}
                            calls
                          </span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-gray-600">Voice Limit:</span>
                          <span className="font-medium">
                            {rateLimit.voice_limit}
                            {' '}
                            clones
                          </span>
                        </div>
                      </div>

                      <div className="mt-4">
                        <Button
                          loading={loadingPlanIndex === index}
                          onClick={() => {
                            if (index === 3) {
                              window.location.href = '/enterprise'
                            }
                            else {
                              setFocusedPlan({
                                rateLimit,
                                index,
                              })

                              if (
                                rateLimit.rate_limit_template === userRateLimitTemplate
                              ) {
                                setCancelModal(true)
                              }
                              else {
                                setPrePurchaseModal(true)
                              }
                            }
                          }}
                          disabled={checkIfPlanDisabled(rateLimit, index).isDisabled}
                          appearance={checkIfPlanDisabled(rateLimit, index).isDisabled ? 'outline' : undefined}
                        >
                          {checkIfPlanDisabled(rateLimit, index).state}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ),
            },
            {
              item: 'Purchase Credits',
              panel: (
                <div className="flex flex-col gap-4 mt-4">
                  <FormProvider {...creditMethods}>
                    <form onSubmit={creditMethods.handleSubmit(onCreditSubmit)}>
                      <div className="bg-white p-4 rounded-lg border flex flex-col gap-2 border-gray-200">
                        <span className="text-black font-semibold text-base">Purchase Credits</span>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                          {creditOptions.map((amt, index) => (
                            <div
                              key={index}
                              onClick={() => creditMethods.setValue('amount', amt.amount)}
                              className={`border ${currAmount === amt.amount ? 'border-blue-500' : 'border-gray-200'} rounded-lg p-3 cursor-pointer hover:border-blue-300 transition-colors`}
                            >
                              <div className="mb-3">
                                <h4 className="font-bold text-lg">
                                  $
                                  {amt.amount}
                                </h4>
                                <p className="text-sm text-gray-600">{amt.description}</p>
                              </div>
                              <Button
                                submit
                                loading={creditPurchaseLoading && clickedCreditIndex === index}
                                onClick={() => setClickedCreditIndex(index)}
                              >
                                Purchase
                              </Button>
                            </div>
                          ))}
                        </div>

                        <div className="mb-4">
                          <h4 className="font-medium mb-2">Custom Amount</h4>
                          <div className="flex gap-2">
                            <div className="flex-grow">
                              <Input
                                fieldName="amount"
                                type="number"
                                min="1"
                                max="10000"
                                label="Amount ($)"
                                style={{ backgroundColor: 'white' }}
                              />
                            </div>
                            <div className="flex items-end">
                              <Button
                                submit
                                loading={creditPurchaseLoading && clickedCreditIndex === 'custom'}
                                onClick={() => setClickedCreditIndex('custom')}
                                disabled={currAmount < 1}
                              >
                                Purchase
                                {' '}
                                {currAmount}
                                {' '}
                                credits ($
                                {currAmount}
                                )
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div>
                          <Checkbox
                            fieldName="save_details"
                            label="Save payment method after checkout"
                          />
                        </div>
                      </div>
                    </form>

                    <span className="flex items-center gap-2 ml-auto">
                      <p className="text-xs text-gray-500">
                        Credit purchases made with a bank account may take a few days to process.
                      </p>
                      <Info size={14} />
                    </span>

                    {/* Credit Purchase History Section */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="mb-4">
                        <span className="text-black font-semibold text-base">Credit Purchase History</span>
                        <p className="text-xs text-gray-500">
                          View your credit top-up history and download invoices.
                        </p>
                      </div>

                      {loadingPurchases
                        ? (
                            <div className="py-8 flex justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                            </div>
                          )
                        : creditPurchases.length === 0
                          ? (
                              <div className="py-8 flex justify-center items-center text-gray-500">
                                <CircleSlash size={16} className="mr-2" />
                                <span>No purchase history found</span>
                              </div>
                            )
                          : (
                              <div className="divide-y divide-gray-100">
                                {creditPurchases.map((payment, index) => (
                                  <div key={index} className="py-4 flex items-center">
                                    <div className="mr-3">
                                      <div className="bg-green-50 p-2 rounded-full">
                                        <DollarSign size={16} className="text-green-600" />
                                      </div>
                                    </div>
                                    <div>
                                      <p className="font-medium">Credit Top-Up</p>
                                      <p className="text-xs text-gray-500">
                                        {formatDistanceToNow(new Date(payment?.created_at), {
                                          addSuffix: true,
                                        })}
                                      </p>
                                    </div>
                                    <div className="ml-auto text-right">
                                      <p className="font-medium">
                                        {payment?.amount?.toFixed(0) || '-'}
                                        {' '}
                                        credits
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        $
                                        {payment?.amount?.toFixed(2)}
                                      </p>
                                    </div>
                                    <Button
                                      className="ml-4"
                                      appearance="outline"
                                      onClick={() => generateInvoicePDF(payment, stripeCustomer)}
                                    >
                                      Invoice
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                    </div>

                    {showCheckout && (
                      <Modal
                        height="auto"
                        open={showCheckout}
                        onClose={() => setShowCheckout(false)}
                      >
                        <div className="p-4">
                          <Checkout
                            amount={currAmount}
                            clientSecret={clientSecret}
                            onSuccess={() => {
                              setShowCheckout(false)
                              toast.success('Credits purchased successfully')
                              handleGetCreditPurchases()
                              refetchCredits()
                            }}
                          />
                        </div>
                      </Modal>
                    )}
                  </FormProvider>
                </div>
              ),
            },
            {
              item: 'Billing Settings',
              panel: (
                <div className="mt-4 flex flex-col gap-4">
                  <BillingAndPayments />
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* Plan Upgrade Modals */}
      <Modal open={prePurchaseModal} onClose={() => setPrePurchaseModal(false)}>
        <div className="flex justify-between items-center mb-2">
          <p className="font-medium text-lg">Confirm Purchase</p>
          <img
            onClick={() => setPrePurchaseModal(false)}
            className="cursor-pointer"
            src={CloseIcon}
            alt="Close"
          />
        </div>
        <p className="text-gray-600 text-sm mb-4">This is a non-refundable purchase.</p>
        <div className="border border-gray-200 rounded p-3 mb-4">
          <div className="flex items-center">
            <img src={ServerIcon} height={18} width={18} alt="Server" />
            <div className="ml-4">
              <p className="font-medium">
                {focusedPlan?.rateLimit?.label}
                {' '}
                Infrastructure
              </p>
              <p className="text-xs text-gray-500">
                {focusedPlan?.rateLimit?.daily}
                {' '}
                calls per day |
                {focusedPlan?.rateLimit?.hourly}
                {' '}
                calls per hour
              </p>
            </div>
            <div className="ml-auto">
              <p className="font-medium">
                {focusedPlan?.rateLimit?.price}
                /m
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => {
            handleSubscribeToPlan(focusedPlan?.rateLimit, focusedPlan?.index)
            setPrePurchaseModal(false)
            setFocusedPlan({ rateLimit: null, index: null })
          }}
        >
          Confirm Purchase
        </Button>
      </Modal>

      <Modal open={cancelModal} onClose={() => setCancelModal(false)}>
        <div className="flex justify-between items-center mb-2">
          <p className="font-medium text-lg">Cancel Plan</p>
          <img
            onClick={() => setCancelModal(false)}
            className="cursor-pointer"
            src={CloseIcon}
            alt="Close"
          />
        </div>
        <p className="text-gray-600 text-sm mb-4">
          You will not be able to undo this once complete. Your plan will automatically be downgraded to START plan.
        </p>
        <div className="border border-gray-200 rounded p-3 mb-4">
          <div className="flex items-center">
            <img src={ServerIcon} height={18} width={18} alt="Server" />
            <div className="ml-4">
              <p className="font-medium">
                {focusedPlan?.rateLimit?.label}
                {' '}
                Infrastructure
              </p>
              <p className="text-xs text-gray-500">
                {focusedPlan?.rateLimit?.daily}
                {' '}
                calls per day |
                {focusedPlan?.rateLimit?.hourly}
                {' '}
                calls per hour
              </p>
            </div>
            <div className="ml-auto">
              <p className="font-medium">Cancel</p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => {
            setLoadingPlanIndex(focusedPlan?.index)
            handleCancelFromPlan()
            setCancelModal(false)
            setFocusedPlan({ rateLimit: null, index: null })
          }}
          style={{
            background: '#e63946',
          }}
        >
          Cancel
          {' '}
          {focusedPlan?.rateLimit?.label}
          {' '}
          Plan
        </Button>
      </Modal>
    </PageWrapper>
  )
}
